import { useState } from 'react';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { 
  Users, 
  Building2, 
  Phone, 
  Mail, 
  Briefcase, 
  MapPin,
  Send,
  Loader2,
  CheckCircle2,
  ChevronRight,
  Rocket
} from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';

const PersonnelRequestPage = () => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    company_name: '',
    contact_person: '',
    phone: '',
    email: '',
    position: '',
    staff_count: '',
    work_type: '',
    experience_required: '',
    description: '',
    location: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    const requiredFields = ['company_name', 'contact_person', 'phone', 'email', 'position', 'staff_count', 'work_type', 'experience_required', 'description', 'location'];
    const missingFields = requiredFields.filter(field => !formData[field]);
    
    if (missingFields.length > 0) {
      toast.error('Lütfen tüm zorunlu alanları doldurun');
      return;
    }

    setLoading(true);
    try {
      await api.post('/personnel-request', {
        ...formData,
        staff_count: parseInt(formData.staff_count),
        experience_required: formData.experience_required === 'yes'
      });
      setSubmitted(true);
      toast.success('Talebiniz başarıyla iletildi!');
    } catch (error) {
      console.error('Error submitting request:', error);
      toast.error('Bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <Layout>
        <div className="min-h-[70vh] flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 py-20">
          <div className="text-center max-w-lg mx-auto px-4">
            <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-12 h-12 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Talebiniz Alındı!
            </h1>
            <p className="text-slate-600 mb-8">
              Personel talebiniz başarıyla iletildi. Uzman ekibimiz en kısa sürede sizinle iletişime geçecektir.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/">
                <Button variant="outline" className="gap-2">
                  Ana Sayfaya Dön
                </Button>
              </Link>
              <Button 
                onClick={() => {
                  setSubmitted(false);
                  setFormData({
                    company_name: '',
                    contact_person: '',
                    phone: '',
                    email: '',
                    position: '',
                    staff_count: '',
                    work_type: '',
                    experience_required: '',
                    description: '',
                    location: ''
                  });
                }}
                className="bg-[#005CB2] hover:bg-[#004B92] gap-2"
              >
                Yeni Talep Oluştur
              </Button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Breadcrumb */}
      <div className="bg-slate-50 border-b">
        <div className="eta-container py-4">
          <div className="flex items-center gap-2 text-sm">
            <Link to="/" className="text-[#005CB2] hover:underline">Ana Sayfa</Link>
            <ChevronRight className="w-4 h-4 text-slate-400" />
            <span className="text-slate-600">Personel Talebi</span>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#005CB2] to-[#003d75] py-16">
        <div className="eta-container text-center text-white">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-full mb-6">
            <Users className="w-8 h-8" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Personel Talep Formu
          </h1>
          <p className="text-white/80 max-w-2xl mx-auto text-lg">
            Aşağıdaki formu doldurarak personel taleplerinizi bize iletebilirsiniz. 
            Uzman ekibimiz, işinize en uygun adayları belirleyerek size en iyi çözümleri sunacaktır!
          </p>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-16 bg-slate-50">
        <div className="eta-container">
          <div className="max-w-3xl mx-auto">
            <Card className="shadow-xl border-0">
              <CardHeader className="bg-slate-50 border-b">
                <CardTitle className="flex items-center gap-3 text-xl">
                  <Rocket className="w-6 h-6 text-[#005CB2]" />
                  Talep Bilgileri
                </CardTitle>
              </CardHeader>
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-8">
                  {/* Company Information */}
                  <div className="space-y-6">
                    <h3 className="font-semibold text-slate-900 flex items-center gap-2 pb-2 border-b">
                      <Building2 className="w-5 h-5 text-[#005CB2]" />
                      Firma Bilgileri
                    </h3>
                    
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="company_name" className="font-semibold">
                          Firma Adı <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="company_name"
                          name="company_name"
                          value={formData.company_name}
                          onChange={handleChange}
                          placeholder="Firma adını giriniz"
                          required
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="contact_person" className="font-semibold">
                          Yetkili Kişi Adı <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="contact_person"
                          name="contact_person"
                          value={formData.contact_person}
                          onChange={handleChange}
                          placeholder="Ad Soyad"
                          required
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="phone" className="font-semibold flex items-center gap-2">
                          <Phone className="w-4 h-4" />
                          Telefon Numarası <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="05XX XXX XX XX"
                          required
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="email" className="font-semibold flex items-center gap-2">
                          <Mail className="w-4 h-4" />
                          E-Posta Adresi <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="ornek@firma.com"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  {/* Position Information */}
                  <div className="space-y-6">
                    <h3 className="font-semibold text-slate-900 flex items-center gap-2 pb-2 border-b">
                      <Briefcase className="w-5 h-5 text-[#005CB2]" />
                      Pozisyon Bilgileri
                    </h3>
                    
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="position" className="font-semibold">
                          Personel Pozisyonu <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="position"
                          name="position"
                          value={formData.position}
                          onChange={handleChange}
                          placeholder="Örn: Depo Elemanı, Forklift Operatörü"
                          required
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="staff_count" className="font-semibold">
                          Personel Sayısı <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="staff_count"
                          name="staff_count"
                          type="number"
                          min="1"
                          value={formData.staff_count}
                          onChange={handleChange}
                          placeholder="Kaç personel?"
                          required
                        />
                      </div>
                    </div>

                    <div className="space-y-3">
                      <Label className="font-semibold">
                        Çalışma Şekli <span className="text-red-500">*</span>
                      </Label>
                      <RadioGroup 
                        value={formData.work_type} 
                        onValueChange={(value) => setFormData(prev => ({ ...prev, work_type: value }))}
                        className="grid grid-cols-2 md:grid-cols-4 gap-4"
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="Tam Zamanlı" id="fulltime" />
                          <Label htmlFor="fulltime" className="cursor-pointer">Tam Zamanlı</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="Yarı Zamanlı" id="parttime" />
                          <Label htmlFor="parttime" className="cursor-pointer">Yarı Zamanlı</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="Freelance" id="freelance" />
                          <Label htmlFor="freelance" className="cursor-pointer">Freelance</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="Dönemsel" id="seasonal" />
                          <Label htmlFor="seasonal" className="cursor-pointer">Dönemsel</Label>
                        </div>
                      </RadioGroup>
                    </div>

                    <div className="space-y-3">
                      <Label className="font-semibold">
                        Deneyim Şartı <span className="text-red-500">*</span>
                      </Label>
                      <RadioGroup 
                        value={formData.experience_required} 
                        onValueChange={(value) => setFormData(prev => ({ ...prev, experience_required: value }))}
                        className="flex gap-6"
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="yes" id="exp_yes" />
                          <Label htmlFor="exp_yes" className="cursor-pointer">Evet, deneyim gerekli</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="no" id="exp_no" />
                          <Label htmlFor="exp_no" className="cursor-pointer">Hayır, deneyim gerekmez</Label>
                        </div>
                      </RadioGroup>
                    </div>
                  </div>

                  {/* Additional Information */}
                  <div className="space-y-6">
                    <h3 className="font-semibold text-slate-900 flex items-center gap-2 pb-2 border-b">
                      <MapPin className="w-5 h-5 text-[#005CB2]" />
                      Ek Bilgiler
                    </h3>
                    
                    <div className="space-y-2">
                      <Label htmlFor="location" className="font-semibold">
                        Çalışma Lokasyonu <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="location"
                        name="location"
                        value={formData.location}
                        onChange={handleChange}
                        placeholder="Örn: İstanbul - Tuzla"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="description" className="font-semibold">
                        İlan Açıklaması <span className="text-red-500">*</span>
                      </Label>
                      <Textarea
                        id="description"
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        placeholder="Pozisyon hakkında detaylı bilgi, aranan özellikler, iş tanımı vb."
                        rows={5}
                        required
                      />
                    </div>
                  </div>

                  {/* Submit Button */}
                  <div className="pt-4">
                    <Button 
                      type="submit" 
                      disabled={loading}
                      className="w-full bg-[#005CB2] hover:bg-[#004B92] text-white py-6 text-lg font-semibold gap-2"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Gönderiliyor...
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5" />
                          Talebi Gönder
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default PersonnelRequestPage;
